import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { clearCache, readCache, writeCache } from "@/lib/localStore";

const WORD_S = 0.03;

// Marks turns that arrive after the first poll as fresh, with a staggered delay so a chunk "types" in turn by turn.
function markFresh(batch) {
  let delay = 0;
  const total = batch.reduce((n, t) => n + (t.text || "").split(/\s+/).length * WORD_S + 0.25, 0);
  const scale = total > 8 ? 8 / total : 1;
  return batch.map((t) => {
    const out = { ...t, _fresh: true, _delay: delay };
    delay += ((t.text || "").split(/\s+/).length * WORD_S + 0.25) * scale;
    return out;
  });
}

const sortTurns = (list) => list.sort((a, b) => a.t - b.t || a.seq - b.seq);
const CACHE_TURNS = 800; // offline view keeps the latest lines; a full reload refetches everything

export function useSessionPoll(id) {
  const cached = useRef(readCache(id));
  const [turns, setTurns] = useState(() => cached.current?.turns || []);
  const [meta, setMeta] = useState(() => cached.current?.meta || null);
  const [gone, setGone] = useState(false);
  const [connected, setConnected] = useState(true);
  const [synced, setSynced] = useState(false);
  // A trimmed cache can't be continued from its seq: fetch everything once when online.
  const seqRef = useRef(cached.current?.truncated ? 0 : cached.current?.seq || 0);
  const goneRef = useRef(false);
  const firstRef = useRef(true);
  const inFlight = useRef(false);
  const revRef = useRef(null); // speaker_rev: a renamed voice changes old lines, so the whole transcript is reloaded

  const refresh = useCallback(async () => {
    if (goneRef.current || inFlight.current) return;
    inFlight.current = true;
    try {
      let { data } = await api.get(`/sessions/${id}/poll`, { params: { after: seqRef.current } });
      const renamed = revRef.current !== null && (data.meta?.speaker_rev ?? 0) !== revRef.current;
      if (data.seq < seqRef.current || (renamed && seqRef.current !== 0)) {
        ({ data } = await api.get(`/sessions/${id}/poll`, { params: { after: 0 } }));
        seqRef.current = 0;
        if (!renamed) setTurns([]);
      }
      revRef.current = data.meta?.speaker_rev ?? 0;
      const replace = seqRef.current === 0;
      if (data.turns.length) {
        const batch = firstRef.current || replace ? data.turns : markFresh(data.turns);
        setTurns((prev) => sortTurns(replace ? batch : [...prev.filter((p) => !batch.some((b) => b.seq === p.seq)), ...batch]));
      }
      firstRef.current = false;
      seqRef.current = data.seq;
      setMeta(data.meta);
      setConnected(true);
      setSynced(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        goneRef.current = true;
        setGone(true);
      } else if (!e?.response) {
        setConnected(false);
      }
    } finally {
      inFlight.current = false;
    }
  }, [id]);

  const metaRef = useRef(meta);
  metaRef.current = meta;
  useEffect(() => {
    if (!metaRef.current) return undefined;
    if (metaRef.current.private) {
      clearCache(id); // a private meeting is never copied into this browser
      return undefined;
    }
    const timer = setTimeout(() => {
      const keep = turns.slice(-CACHE_TURNS).map(({ _fresh, _delay, ...t }) => t);
      writeCache(id, { turns: keep, meta: metaRef.current, seq: seqRef.current, truncated: turns.length > CACHE_TURNS });
    }, 3000);
    return () => clearTimeout(timer);
  }, [id, turns]);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 2000);
    return () => clearInterval(iv);
  }, [refresh]);

  const markGone = () => {
    goneRef.current = true;
    clearCache(id);
    setGone(true);
  };

  return { turns, meta, gone, connected, synced, refresh, setMeta, markGone };
}
